import React, { useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchContent } from "@/lib/api";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import Hero from "@/components/sections/Hero";
import TrustBar from "@/components/sections/TrustBar";
import ServiceGrid from "@/components/sections/ServiceGrid";
import TestimonialGrid from "@/components/sections/TestimonialGrid";
import GoogleReviews from "@/components/sections/GoogleReviews";
import GoogleRatingStrip from "@/components/sections/GoogleRatingStrip";
import ConsulateBanner from "@/components/sections/ConsulateBanner";
import MediaStrip from "@/components/sections/MediaStrip";
import CtaBand from "@/components/sections/CtaBand";
import FaqAccordion from "@/components/sections/FaqAccordion";
import ProseBlock from "@/components/sections/ProseBlock";
import Breadcrumbs from "@/components/sections/Breadcrumbs";
import ServiceSidebar from "@/components/sections/ServiceSidebar";
import { UI } from "@/content/site";

/**
 * Generate a stable-ish React key for a section given its content.
 * Falls back to type+index if no identifying heading exists.
 */
function sectionKey(section, index) {
  const head = section.heading || section.h || "";
  return `${section.kind}::${head || index}`;
}

/** Per-section renderers keyed by section.kind. */
function ProseSection({ section, isLast }) {
  return (
    <section className="gw-container gw-section" style={isLast ? {} : { paddingBottom: "var(--gw-space-8)" }}>
      <ProseBlock section={section} />
    </section>
  );
}

function ConsulateSection({ section, lang }) {
  return <ConsulateBanner section={section} lang={lang} />;
}

function ServiceGridSection({ section, lang, isHome }) {
  return (
    <section className={isHome ? "gw-section gw-section-cream" : "gw-section"}>
      <div className="gw-container">
        <ServiceGrid heading={section.heading} lang={lang} />
      </div>
    </section>
  );
}

function TestimonialsSection({ section, lang }) {
  return (
    <section className="gw-section">
      <div className="gw-container">
        <TestimonialGrid section={section} lang={lang} />
      </div>
    </section>
  );
}

/** [Phase 25] Live Google Business Profile rating + review highlights. */
function GoogleReviewsSection({ section, lang }) {
  return (
    <section className="gw-section gw-section-cream">
      <div className="gw-container">
        <GoogleReviews section={section} lang={lang} />
      </div>
    </section>
  );
}

function FaqSection({ section }) {
  return (
    <section className="gw-container gw-section">
      <FaqAccordion section={section} />
    </section>
  );
}

function CtaSection({ section, lang }) {
  return <CtaBand section={section} lang={lang} />;
}

function LocationGridSection({ section }) {  return (
    <section className="gw-section gw-section-cream">
      <div className="gw-container">
        {section.heading && (
          <h2 style={{ marginBottom: "var(--gw-space-5)" }}>{section.heading}</h2>
        )}
        <ul
          data-testid="location-grid"
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "var(--gw-space-4)",
          }}
        >
          {(section.items || []).map((loc) => (
            <li key={loc.href}>
              <LocationCard loc={loc} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function LocationCard({ loc }) {
  const slug = loc.href.split("/").pop();
  const handleEnter = (e) => {
    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
  };
  const handleLeave = (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "none";
  };
  return (
    <Link
      to={loc.href}
      data-testid={`location-card-${slug}`}
      style={{
        display: "block",
        padding: "var(--gw-space-4)",
        background: "#fff",
        border: "1px solid var(--gw-color-border, rgba(0,0,0,0.08))",
        borderRadius: "var(--gw-radius-md, 12px)",
        textDecoration: "none",
        color: "inherit",
        transition: "transform 200ms ease, box-shadow 200ms ease",
      }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <h3 style={{ margin: "0 0 var(--gw-space-2)", fontSize: "1.15rem" }}>{loc.label}</h3>
      {loc.blurb && (
        <p style={{ margin: 0, opacity: 0.8, fontSize: "0.95rem" }}>{loc.blurb}</p>
      )}
    </Link>
  );
}

function AttorneyCalloutSection({ section, lang }) {
  const attorneys = section.attorneys || [];
  if (!attorneys.length) return null;
  const cta = lang === "en" ? "Meet the attorney" : "Conozca a la abogada";
  return (
    <section
      className="gw-section gw-attorney-callout"
      data-testid="attorney-callout"
      aria-label={section.heading}
    >
      <div className="gw-attorney-callout-inner">
        {section.heading && <h2>{section.heading}</h2>}
        {(section.paragraphs || []).map((p, i) => (
          <p key={i} className="gw-attorney-callout-lede">{p}</p>
        ))}
        <ul className="gw-attorney-callout-list">
          {attorneys.map((att) => (
            <li key={att.href}>
              <Link
                to={att.href}
                className="gw-attorney-callout-card"
                data-testid="attorney-callout-link"
                rel="author"
              >
                <strong>{att.name}</strong>
                {att.role && (
                  <span className="gw-attorney-callout-role">{att.role}</span>
                )}
                <span className="gw-attorney-callout-cta">{cta} →</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <style>{`
        .gw-attorney-callout { background: #FFFFFF; border-top: 1px solid var(--gw-border, rgba(0,0,0,0.08)); border-bottom: 1px solid var(--gw-border, rgba(0,0,0,0.08)); padding-top: var(--gw-space-8); padding-bottom: var(--gw-space-8); margin-top: var(--gw-space-8); }
        .gw-attorney-callout-inner { max-width: 900px; padding: 0 var(--gw-space-6); margin: 0 auto; }
        .gw-attorney-callout h2 { color: var(--gw-primary-dark); margin: 0 0 var(--gw-space-3); font-size: 1.4rem; }
        .gw-attorney-callout-lede { color: var(--gw-muted, rgba(0,0,0,0.65)); font-size: 1rem; line-height: 1.6; margin: 0 0 var(--gw-space-5); max-width: 720px; }
        .gw-attorney-callout-list { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: var(--gw-space-4); }
        .gw-attorney-callout-card { display: flex; flex-direction: column; gap: 4px; padding: var(--gw-space-5); background: var(--gw-color-cream, #F5EFE3); border: 1px solid var(--gw-border, rgba(0,0,0,0.08)); border-radius: var(--gw-radius, 12px); color: var(--gw-text, inherit); text-decoration: none; transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease; min-height: 56px; }
        .gw-attorney-callout-card:hover, .gw-attorney-callout-card:focus-visible { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(59,10,20,0.10); border-color: var(--gw-accent); outline: none; }
        .gw-attorney-callout-card strong { color: var(--gw-primary-dark); font-family: var(--gw-font-serif); font-size: 1.1rem; }
        .gw-attorney-callout-role { color: var(--gw-muted, rgba(0,0,0,0.65)); font-size: 0.9rem; }
        .gw-attorney-callout-cta { color: var(--gw-primary); font-weight: 600; font-size: 0.9rem; margin-top: 6px; }
        @media (max-width: 640px) {
          .gw-attorney-callout { padding-top: var(--gw-space-6); padding-bottom: var(--gw-space-6); margin-top: var(--gw-space-6); }
          .gw-attorney-callout-inner { padding: 0 var(--gw-space-4); }
        }
      `}</style>
    </section>
  );
}

function RelatedServicesSection({ section, lang }) {
  const services = section.services || [];
  const blogs = section.blogs || [];
  if (!services.length && !blogs.length) return null;
  return (
    <section
      className="gw-section gw-related-services"
      data-testid="related-services"
      aria-label={section.heading_services}
    >
      <div className="gw-related-services-inner">
        {services.length > 0 && (
          <div className="gw-related-services-block" data-testid="related-services-sibling-block">
            <h2>{section.heading_services}</h2>
            <ul className="gw-related-services-grid">
              {services.map((svc) => (
                <li key={svc.key}>
                  <Link
                    to={svc.href}
                    className="gw-related-services-card"
                    data-testid={`related-service-${svc.key}`}
                  >
                    <span className="gw-related-services-card-title">{svc.title}</span>
                    <span className="gw-related-services-card-arrow" aria-hidden="true">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {blogs.length > 0 && (
          <div className="gw-related-services-block" data-testid="related-services-blog-block">
            <h2>{section.heading_blogs}</h2>
            <ul className="gw-related-blogs-list">
              {blogs.map((post) => (
                <li key={post.slug}>
                  <Link
                    to={post.href}
                    className="gw-related-blog-card"
                    data-testid={`related-blog-${post.slug}`}
                  >
                    <strong>{post.title}</strong>
                    {post.description && (
                      <span className="gw-related-blog-desc">{post.description}</span>
                    )}
                    <span className="gw-related-blog-cta">
                      {lang === "en" ? "Read the article →" : "Leer el artículo →"}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <style>{`
        .gw-related-services { background: #F5EFE3; padding-top: var(--gw-space-8); padding-bottom: var(--gw-space-8); border-radius: var(--gw-radius-lg); margin-top: var(--gw-space-8); }
        .gw-related-services-inner { padding: 0 var(--gw-space-6); }
        @media (max-width: 640px) {
          .gw-related-services { padding-top: var(--gw-space-6); padding-bottom: var(--gw-space-6); margin-top: var(--gw-space-6); }
          .gw-related-services-inner { padding: 0 var(--gw-space-4); }
        }
        .gw-related-services-block { margin-bottom: var(--gw-space-7); }
        .gw-related-services-block:last-child { margin-bottom: 0; }
        .gw-related-services-block h2 { color: var(--gw-primary-dark); margin-bottom: var(--gw-space-4); font-size: 1.4rem; }
        .gw-related-services-grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--gw-space-3); }
        .gw-related-services-card { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: var(--gw-space-4); background: #fff; border-radius: var(--gw-radius); border: 1px solid var(--gw-border); color: var(--gw-primary-dark); text-decoration: none; transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease; font-family: var(--gw-font-serif); font-weight: 600; min-height: 56px; -webkit-tap-highlight-color: rgba(122, 27, 42, 0.12); }
        .gw-related-services-card:hover, .gw-related-services-card:focus-visible { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(59,10,20,0.10); border-color: var(--gw-accent); outline: none; }
        .gw-related-services-card-arrow { color: var(--gw-accent); font-weight: 700; }
        .gw-related-blogs-list { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: var(--gw-space-4); }
        .gw-related-blog-card { display: flex; flex-direction: column; gap: 8px; padding: var(--gw-space-5); background: #fff; border-radius: var(--gw-radius); border: 1px solid var(--gw-border); color: var(--gw-text); text-decoration: none; transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease; min-height: 48px; -webkit-tap-highlight-color: rgba(122, 27, 42, 0.12); }
        .gw-related-blog-card:hover, .gw-related-blog-card:focus-visible { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(59,10,20,0.10); border-color: var(--gw-accent); outline: none; }
        .gw-related-blog-card strong { color: var(--gw-primary-dark); font-family: var(--gw-font-serif); font-size: 1.02rem; }
        .gw-related-blog-desc { color: var(--gw-muted); font-size: 0.9rem; line-height: 1.5; }
        .gw-related-blog-cta { color: var(--gw-primary); font-weight: 600; font-size: 0.9rem; margin-top: auto; }
      `}</style>
    </section>
  );
}

function ReviewMetaSection({ section }) {
  const reviewer = section.reviewer || {};
  const reviewed = section.reviewed_on || {};
  const jurisdiction = section.jurisdiction || {};
  const sources = section.sources || {};
  const sourceItems = sources.items || [];
  return (
    <section
      className="gw-section gw-review-meta"
      data-testid="review-meta"
      aria-label={section.heading}
    >
      <div className="gw-review-meta-inner">
        {section.heading && <h2>{section.heading}</h2>}
        <dl className="gw-review-meta-list">
          {reviewer.name && (
            <div className="gw-review-meta-item">
              <dt>{reviewer.label}</dt>
              <dd>
                {reviewer.href ? (
                  <Link
                    to={reviewer.href}
                    rel="author"
                    data-testid="review-meta-reviewer-link"
                  >
                    {reviewer.name}
                  </Link>
                ) : (
                  reviewer.name
                )}
              </dd>
            </div>
          )}
          {reviewed.date && (
            <div className="gw-review-meta-item">
              <dt>{reviewed.label}</dt>
              <dd>
                <time dateTime={reviewed.date}>{reviewed.date}</time>
              </dd>
            </div>
          )}
          {jurisdiction.value && (
            <div className="gw-review-meta-item">
              <dt>{jurisdiction.label}</dt>
              <dd>{jurisdiction.value}</dd>
            </div>
          )}
        </dl>
        {sourceItems.length > 0 && (
          <>
            <h3 className="gw-review-meta-sources-heading">{sources.label}</h3>
            <ul className="gw-review-meta-sources">
              {sourceItems.map((item) => (
                <li key={item.url}>
                  <a
                    href={item.url}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    data-testid="review-meta-source-link"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
        {section.disclaimer && (
          <p className="gw-review-meta-disclaimer">
            <em>{section.disclaimer}</em>
          </p>
        )}
      </div>
      <style>{`
        .gw-review-meta { background: #FFFFFF; padding-top: var(--gw-space-6); padding-bottom: var(--gw-space-6); border-top: 1px solid var(--gw-border, rgba(0,0,0,0.08)); }
        .gw-review-meta-inner { max-width: 900px; padding: 0 var(--gw-space-6); margin: 0 auto; }
        .gw-review-meta h2 { color: var(--gw-primary-dark); margin: 0 0 var(--gw-space-4); font-size: 1.25rem; }
        .gw-review-meta-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--gw-space-3) var(--gw-space-6); margin: 0 0 var(--gw-space-4); }
        .gw-review-meta-item dt { color: var(--gw-muted, rgba(0,0,0,0.62)); font-size: 0.85rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 4px; }
        .gw-review-meta-item dd { color: var(--gw-text, inherit); font-size: 1rem; margin: 0; }
        .gw-review-meta-item dd a { color: var(--gw-primary); font-weight: 600; text-decoration: none; border-bottom: 1px solid transparent; transition: border-color 180ms ease; }
        .gw-review-meta-item dd a:hover, .gw-review-meta-item dd a:focus-visible { border-bottom-color: var(--gw-primary); outline: none; }
        .gw-review-meta-sources-heading { color: var(--gw-primary-dark); font-size: 1rem; margin: var(--gw-space-3) 0 var(--gw-space-2); }
        .gw-review-meta-sources { list-style: disc; padding-left: 1.25em; margin: 0 0 var(--gw-space-4); color: var(--gw-text, inherit); }
        .gw-review-meta-sources a { color: var(--gw-primary); text-decoration: none; border-bottom: 1px solid transparent; transition: border-color 180ms ease; }
        .gw-review-meta-sources a:hover, .gw-review-meta-sources a:focus-visible { border-bottom-color: var(--gw-primary); outline: none; }
        .gw-review-meta-disclaimer { color: var(--gw-muted, rgba(0,0,0,0.62)); font-size: 0.9rem; line-height: 1.55; margin: 0; }
      `}</style>
    </section>
  );
}

/** Dispatch table: section.kind -> renderer component. */
const SECTION_RENDERERS = {
  prose: ProseSection,
  consulate: ConsulateSection,
  "service-grid": ServiceGridSection,
  testimonials: TestimonialsSection,
  "google-reviews": GoogleReviewsSection,
  "google-rating-strip": GoogleRatingStrip,
  faq: FaqSection,
  cta: CtaSection,
  "location-grid": LocationGridSection,
  portrait: PortraitSection,
  "photo-gallery": PhotoGallerySection,
  "related-services": RelatedServicesSection,
  "attorney-callout": AttorneyCalloutSection,
  "review-meta": ReviewMetaSection,
};

/** Attorney portrait card (used on bio pages). */
function PortraitSection({ section }) {
  return (
    <section className="gw-section" data-testid="attorney-portrait">
      <div className="gw-container" style={{
        display: "grid",
        gridTemplateColumns: "minmax(240px, 340px) 1fr",
        gap: "var(--gw-space-8)",
        alignItems: "center",
      }}>
        <figure style={{ margin: 0 }}>
          <div style={{
            background: "linear-gradient(135deg, #7A1B2A 0%, #3B0A14 100%)",
            padding: "10px",
            borderRadius: "var(--gw-radius-lg, 16px)",
            boxShadow: "0 20px 60px rgba(59, 10, 20, 0.22)",
            border: "1px solid rgba(212, 160, 23, 0.35)",
          }}>
            <img
              src={section.image}
              alt={section.alt || ""}
              width="320"
              height="400"
              loading="eager"
              style={{
                width: "100%",
                aspectRatio: "4/5",
                objectFit: "cover",
                objectPosition: "center 15%",
                display: "block",
                borderRadius: "var(--gw-radius, 12px)",
              }}
            />
          </div>
          <figcaption style={{ marginTop: "var(--gw-space-4)", textAlign: "center" }}>
            {section.caption_top && (
              <strong style={{
                display: "block",
                fontFamily: "var(--gw-font-serif)",
                color: "var(--gw-primary-dark)",
                fontSize: "1.05rem",
              }}>
                {section.caption_top}
              </strong>
            )}
            {section.caption_bottom && (
              <span style={{
                color: "var(--gw-muted)",
                fontSize: "0.85rem",
                display: "block",
                marginTop: 4,
              }}>
                {section.caption_bottom}
              </span>
            )}
          </figcaption>
        </figure>
        <div style={{ minWidth: 0 }} />
      </div>
      <style>{`
        @media (max-width: 800px) {
          [data-testid="attorney-portrait"] > .gw-container { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

/** Photo gallery / awards row. */
function PhotoGallerySection({ section }) {
  return (
    <section className="gw-section gw-section-cream" data-testid="photo-gallery">
      <div className="gw-container">
        {section.heading && (
          <h2 style={{ marginBottom: "var(--gw-space-5)" }}>{section.heading}</h2>
        )}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: "var(--gw-space-5)",
        }}>
          {(section.items || []).map((item) => (
            <figure key={item.image} style={{ margin: 0 }}>
              <div style={{
                aspectRatio: "3/4",
                overflow: "hidden",
                borderRadius: "var(--gw-radius, 12px)",
                background: "#fff",
                border: "1px solid var(--gw-border, rgba(0,0,0,0.08))",
                boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
              }}>
                <img
                  src={item.image}
                  alt={item.alt || ""}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center 15%",
                    display: "block",
                  }}
                />
              </div>
              {item.caption && (
                <figcaption style={{
                  marginTop: 10,
                  fontSize: "0.9rem",
                  color: "var(--gw-muted)",
                  textAlign: "center",
                }}>
                  {item.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Render a list of content sections. */
function SectionsRenderer({ sections, lang, isHome }) {
  if (!sections?.length) return null;
  return (
    <>
      {sections.map((section, index) => {
        const Renderer = SECTION_RENDERERS[section.kind];
        if (!Renderer) return null;
        return (
          <Renderer
            key={sectionKey(section, index)}
            section={section}
            lang={lang}
            isHome={isHome}
            isLast={index === sections.length - 1}
          />
        );
      })}
    </>
  );
}

/** Header for non-home pages (breadcrumbs + H1). */
function PageHeader({ page, lang }) {
  return (
    <section className="gw-container gw-section" style={{ paddingBottom: 0 }}>
      {page.breadcrumbs && <Breadcrumbs items={page.breadcrumbs} lang={lang} />}
      <h1>{page.h1 || page.title}</h1>
    </section>
  );
}

/** Body layout for service pages (main + sidebar). */
function ServicePageBody({ page, lang, sourcePage }) {
  return (
    <div className="gw-container">
      <div className="gw-service-layout">
        <div className="gw-service-main">
          {page.quotable_answer && (
            <aside
              className="gw-quotable speakable-answer"
              data-testid="answer-first-module"
              aria-label={lang === "es" ? "Respuesta directa" : "Direct answer"}
            >
              <p>{page.quotable_answer}</p>
            </aside>
          )}
          <SectionsRenderer sections={page.sections} lang={lang} />
        </div>
        <aside className="gw-service-aside">
          <ServiceSidebar lang={lang} sourcePage={sourcePage} />
        </aside>
      </div>
    </div>
  );
}

/** Home-only bottom CTA band ("Ready to talk?"). */
function HomeCtaBand({ lang }) {
  const ui = UI[lang];
  return (
    <section className="gw-section gw-section-dark" style={{ textAlign: "center" }}>
      <div className="gw-container">
        <span className="gw-eyebrow" style={{ color: "#F1C232" }}>
          {lang === "en" ? "Ready to talk?" : "¿Listo para hablar?"}
        </span>
        <h2 style={{ color: "#fff" }}>
          {lang === "en"
            ? "Take control of your legal situation — today."
            : "Tome el control de su situación legal, hoy."}
        </h2>
        <p style={{ maxWidth: 640, margin: "0 auto var(--gw-space-6)", color: "rgba(255,255,255,0.9)" }}>
          {lang === "en"
            ? "Free bilingual consultation. We respond within one business day. Bishop Arts District, Dallas."
            : "Consulta gratis bilingüe. Respondemos en un día hábil. Distrito Bishop Arts, Dallas."}
        </p>
        <div style={{ display: "flex", gap: "var(--gw-space-3)", justifyContent: "center", flexWrap: "wrap" }}>
          <Link to={ui.contactUrl} className="gw-btn gw-btn-accent gw-btn-lg">{ui.ctaConsultation}</Link>
          <a href="tel:+12144666699" className="gw-btn gw-btn-outline gw-btn-lg">{ui.ctaCall}</a>
        </div>
      </div>
    </section>
  );
}

/**
 * Normalize a pathname (drop trailing slash except at root).
 */
function normalizePath(pathname) {
  if (pathname !== "/" && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/** Loading/error states. */
function LoadingState() {
  return <div className="gw-container gw-section"><p>Loading…</p></div>;
}

function NotFoundState({ lang }) {
  return (
    <div className="gw-container gw-section">
      <h1>{lang === "es" ? "Página no encontrada" : "Page not found"}</h1>
      <p>
        <Link to={lang === "es" ? "/es" : "/"}>
          {lang === "es" ? "Ir al inicio" : "Go home"}
        </Link>
      </p>
    </div>
  );
}

export default function PageRenderer() {
  const { pathname } = useLocation();
  const lang = useLang();
  const normalized = useMemo(() => normalizePath(pathname), [pathname]);

  // Preloaded content injected by the SSR renderer as
  // ``window.__GW_INITIAL_CONTENT__[path]``. When present, react-query
  // treats it as the initial cache value so the very first paint has full
  // hero + body content, not just the nav shell. Eliminates the ~500ms
  // "content flash" between mount and API response on cold loads.
  const preloaded = useMemo(() => {
    if (typeof window === "undefined") return undefined;
    const pre = window.__GW_INITIAL_CONTENT__;
    if (pre && typeof pre === "object" && pre[normalized]) return pre[normalized];
    return undefined;
  }, [normalized]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["content", normalized],
    queryFn: () => fetchContent(normalized),
    staleTime: 5 * 60_000,
    initialData: preloaded,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [normalized]);

  if (isLoading) return <LoadingState />;
  if (isError || !data?.page) return <NotFoundState lang={lang} />;

  const { page, schema, kind } = data;
  const isServicePage = kind === "service";
  const isHome = kind === "home";
  const showQuotableStandalone = page.quotable_answer && !isServicePage;

  return (
    <>
      <Seo page={page} path={normalized} lang={lang} pair={data.pair} schema={schema} />

      {isHome && <Hero page={page} lang={lang} />}
      {isHome && <TrustBar lang={lang} />}

      {!isHome && <PageHeader page={page} lang={lang} />}

      {showQuotableStandalone && (
        <div className="gw-container" style={{ paddingTop: 0 }}>
          <aside
            className="gw-quotable speakable-answer"
            data-testid="answer-first-module"
            aria-label={lang === "es" ? "Respuesta directa" : "Direct answer"}
          >
            <p>{page.quotable_answer}</p>
          </aside>
        </div>
      )}

      {isServicePage && (
        <ServicePageBody page={page} lang={lang} sourcePage={normalized} />
      )}

      {!isServicePage && (
        <SectionsRenderer sections={page.sections} lang={lang} isHome={isHome} />
      )}

      {isHome && <MediaStrip lang={lang} />}
      {isHome && <HomeCtaBand lang={lang} />}
    </>
  );
}
